import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { transcribeVoice } from 'api/voice'
import { settingsActions } from 'store/settings'
import { audioPlayer } from 'services/audio-player'
import { showErrorToast } from 'utils/toast'
import { useVoiceRecorder } from 'hooks/useVoiceRecorder'

type UseVoiceComposerOptions = {
  chatId: string | undefined
  /** Called with the non-empty transcription; the caller sends it as a voice question. */
  onTranscribed: (text: string) => Promise<void>
}

/**
 * Voice question flow for the composer: record -> transcribe -> hand the text to the caller.
 * Owns the recorder, the transcribe request and its error handling (403 hides the mic via
 * the settings flag), the autoplay unlock, and the Esc-to-cancel binding.
 */
export const useVoiceComposer = ({ chatId, onTranscribed }: UseVoiceComposerOptions) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [transcribing, setTranscribing] = useState(false)

  const onVoiceRecorded = useCallback(
    async (blob: Blob) => {
      if (!chatId) return
      setTranscribing(true)
      try {
        const text = (await transcribeVoice(chatId, blob)).trim()
        if (!text) {
          showErrorToast(t('voiceNothingHeard'))
          return
        }
        await onTranscribed(text)
      } catch (e) {
        const status = isAxiosError(e) ? e.response?.status : undefined
        if (status === 403) {
          // Tenant has no voice: hide the mic from now on.
          dispatch(settingsActions.setVoiceEnabled(false))
        } else if (status === 429) {
          showErrorToast(t('voiceRateLimited'))
        } else {
          showErrorToast(t('voiceTranscribeFailed'))
        }
        console.error({ e })
      } finally {
        setTranscribing(false)
      }
    },
    [chatId, onTranscribed, dispatch, t],
  )

  const recorder = useVoiceRecorder({
    onRecorded: (blob) => void onVoiceRecorded(blob),
    onPermissionDenied: () => showErrorToast(t('voicePermissionDenied')),
  })
  const { start, stop, cancel } = recorder

  const startRecording = useCallback(() => {
    audioPlayer.stopAll() // a new recording interrupts whatever the bot is saying
    start().catch(console.error)
  }, [start])

  const confirm = useCallback(() => {
    // Inside the user gesture: grants autoplay for the TTS segments arriving over WS.
    audioPlayer.unlock()
    stop()
  }, [stop])

  // Esc cancels the recording (same as the ✕ button).
  useEffect(() => {
    if (recorder.state !== 'recording') return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancel()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [recorder.state, cancel])

  return {
    isRecording: recorder.state !== 'idle',
    /** Mic requested but capture not started yet (permission prompt / hardware warm-up). */
    isStarting: recorder.state === 'requesting',
    elapsed: recorder.elapsed,
    stream: recorder.stream,
    transcribing,
    startRecording,
    confirm,
    cancel,
  }
}
